import React from 'react';
import dayjs from 'dayjs';
import { get } from 'lodash-es';
import { CARE_ORDER_BABY_TYPE, } from '@/interface/domain/careOrderBaby';
import { useApiLoadingPool, usePreventInfiniteLoading, useRouter, useScrollToTop, } from '@/util/hook/custom';
import useCallback from '@/util/hook/useCallback';
import useDispatch from '@/util/hook/useDispatch';
import useEffect from '@/util/hook/useEffect';
import useLayoutEffect from '@/util/hook/useLayoutEffect';
import useMemo from '@/util/hook/useMemo';
import useSelector from '@/util/hook/useSelector';
import useState from '@/util/hook/useState';
import { getDefaultPartTimeExpectedStartTime, getPartTimeTimeSlotOptionList, } from '../../util/helper/option';
import { useGetCareClientVisitorProfile } from '../../util/hook/useGetCareClientVisitorProfile';
import { useGetCareOrderPlanOptionList, } from '../../util/hook/useGetCareOrderPlanOptionList';
import { useGetRemoteConfig } from '../../util/hook/useGetRemoteConfig';
import { useTrackBrowserHistory } from '../../util/hook/useTrackBrowserHistory';
import { useValidateCareClientVisitorProfileAndRedirect } from '../../util/hook/useValidateCareClientVisitorProfileAndRedirect';
import { careOrderBabyFormApplyNewPartTimeExpectedServiceHours, careOrderBabyFormApplyNewPartTimeExpectedStartTime, careOrderBabyFormUpdate, careOrderBabyFormUpdateAllDayTimeSlot, selectCareOrderBabyForm, } from '../../redux/module/careOrderBabyForm';
import { selectESTBufferHours, selectESTFurthestDays, selectMaxServiceDays, } from '../../redux/module/remoteConfig';
import { selectCarouselCurrentActiveItemKey, selectCarouselPrevActiveItemKey, trackerCarouselCurrActiveItemKeyUpdate, trackerCarouselPrevActiveItemKeyUpdate, } from '../../redux/module/tracker';
import View from './View';
import { APP } from '../../config/app';
import { POPUP_TYPE } from '../../config/popup';
import { TIME_MANIPULATION } from '../../config/time';
import { SLIDE_SWITCH_TRANSITION_SECOND } from '../../config/ui/time';
import { CareOrderBabyFormUConverter } from '../../util/uiConverter/CareOrderBabyForm';
const getDidCompleteRequiredFormFields = ({ isAsap, initialType, timeSlotList, }) => {
    if (initialType === CARE_ORDER_BABY_TYPE.ALL_DAY) {
        const timeSlot = timeSlotList[0];
        if (isAsap) {
            const didComplete = Boolean(timeSlot && timeSlot.expectedServiceDays);
            return didComplete;
        }
        const didComplete = Boolean(timeSlot && timeSlot.expectedServiceDays && timeSlot.expectedStartTime);
        return didComplete;
    }
    // partTime
    const doComplete = timeSlotList.length > 0;
    return doComplete;
};
const getCanSubtractAllDayExpectedServiceDays = ({ allDayExpectedServiceDays, }) => {
    const canSubtract = allDayExpectedServiceDays > 1;
    return canSubtract;
};
const getCanAddAllDayExpectedServiceDays = ({ allDayExpectedServiceDays, maxServiceDays, }) => {
    const canAdd = allDayExpectedServiceDays < maxServiceDays;
    return canAdd;
};
const getCanSubtractPartTimeExpectedStartTime = ({ partTimeExpectedStartTime, }) => {
    const expectedStartTimeObj = dayjs(partTimeExpectedStartTime);
    const expectedStarTimeAfterSubtraction = expectedStartTimeObj.subtract(TIME_MANIPULATION.MINUTE.UNIT, TIME_MANIPULATION.MINUTE.TYPE);
    const isAvailable = expectedStarTimeAfterSubtraction.date() === expectedStartTimeObj.date();
    return isAvailable;
};
const getCanAddPartTimeExpectedStartTime = ({ partTimeExpectedStartTime, }) => {
    const expectedStartTimeObj = dayjs(partTimeExpectedStartTime);
    const expectedStarTimeAfterAddition = expectedStartTimeObj.add(TIME_MANIPULATION.MINUTE.UNIT, TIME_MANIPULATION.MINUTE.TYPE);
    const isAvailable = expectedStarTimeAfterAddition.date() === expectedStartTimeObj.date();
    return isAvailable;
};
const useUpdateTimeSelectionDataWhenCarouselActiveItemChange = ({ carouselCurrentActiveItemKey, carouselPrevActiveItemKey, defaultPartTimeExpectedStartTime, didSlideCarousel, setIsSectionLoading, careOrderPlanOptionList, dispatch, }) => {
    const currentCareOrderPlan = careOrderPlanOptionList.find((careOrderPlanOption) => careOrderPlanOption.key === carouselCurrentActiveItemKey);
    const prevCareOrderPlan = careOrderPlanOptionList.find((careOrderPlanOption) => careOrderPlanOption.key === carouselPrevActiveItemKey);
    const newInitialType = get(currentCareOrderPlan, 'initialType');
    const prevInitialType = get(prevCareOrderPlan, 'initialType');
    const expectedServiceHours = get(currentCareOrderPlan, 'hour');
    const prevExpectedServiceHours = get(prevCareOrderPlan, 'hour');
    const newCareOrderPlanKey = get(currentCareOrderPlan, 'key');
    // #NOTES
    // we apply useLayoutEffect here to avoid page from flickering
    // when rendering the section loading spinner
    useLayoutEffect(() => {
        // we use 'didSlideCarousel' to prevent the following logic from running on the first render
        if (didSlideCarousel) {
            // case 1. initialType changed
            if (newInitialType !== prevInitialType) {
                setIsSectionLoading(true);
                setTimeout(() => {
                    setIsSectionLoading(false);
                }, SLIDE_SWITCH_TRANSITION_SECOND * 1000);
                dispatch(careOrderBabyFormUpdate({
                    timeSlotList: [],
                    partTimeExpectedStartTime: defaultPartTimeExpectedStartTime,
                }));
            }
            // case 2. expectedServiceHours changed
            if (newInitialType === prevInitialType && expectedServiceHours !== prevExpectedServiceHours) {
                dispatch(careOrderBabyFormApplyNewPartTimeExpectedServiceHours({
                    expectedServiceHours: expectedServiceHours,
                }));
            }
            dispatch(careOrderBabyFormUpdate({
                initialType: newInitialType,
                careOrderPlanKey: newCareOrderPlanKey,
                partTimeExpectedServiceHours: expectedServiceHours,
            }));
        }
    }, [carouselCurrentActiveItemKey, carouselPrevActiveItemKey, didSlideCarousel]);
};
const useResetTimeSelectionDataWhenInitialTypeChange = ({ initialType, timeSlotList, dispatch, defaultPartTimeExpectedStartTime, partTimeExpectedStartTime, }) => {
    useEffect(() => {
        if (initialType === CARE_ORDER_BABY_TYPE.ALL_DAY) {
            dispatch(careOrderBabyFormUpdate({
                partTimeExpectedStartTime: '',
                partTimeExpectedServiceHours: null,
            }));
            if (timeSlotList.length === 0) {
                const defaultAllDayTimeSlotList = [{ expectedStartTime: '', expectedServiceDays: 1 }];
                dispatch(careOrderBabyFormUpdate({
                    timeSlotList: defaultAllDayTimeSlotList,
                }));
            }
        }
        if (initialType === CARE_ORDER_BABY_TYPE.PART_TIME) {
            dispatch(careOrderBabyFormUpdate({
                isAsap: null,
            }));
            if (!partTimeExpectedStartTime) {
                dispatch(careOrderBabyFormUpdate({
                    partTimeExpectedStartTime: defaultPartTimeExpectedStartTime,
                }));
            }
        }
    }, [initialType]);
};
export default function Controller() {
    const { isLoading, setApiProcessingPool, setApiDonePool } = useApiLoadingPool(true);
    const [didSlideCarousel, setDidSlideCarousel] = useState(false);
    const [popupType, setPopupType] = useState(POPUP_TYPE.CLOSE);
    const careOrderBabyForm = useSelector(selectCareOrderBabyForm);
    const carouselCurrentActiveItemKey = useSelector(selectCarouselCurrentActiveItemKey);
    const carouselPrevActiveItemKey = useSelector(selectCarouselPrevActiveItemKey);
    const maxServiceDays = useSelector(selectMaxServiceDays);
    const ESTBufferHours = useSelector(selectESTBufferHours);
    const ESTFurthestDays = useSelector(selectESTFurthestDays);
    const isAllDay = careOrderBabyForm.initialType === CARE_ORDER_BABY_TYPE.ALL_DAY;
    const allDayTimeSlot = careOrderBabyForm.timeSlotList[0];
    const allDayExpectedServiceDays = get(allDayTimeSlot, 'expectedServiceDays');
    const partTimeExpectedStartTime = careOrderBabyForm.partTimeExpectedStartTime;
    const partTimeExpectedServiceHours = careOrderBabyForm.partTimeExpectedServiceHours;
    const { careOrderPlanOptionList } = useGetCareOrderPlanOptionList({
        setApiProcessingPool,
        setApiDonePool,
    });
    const { navigate } = useRouter();
    const dispatch = useDispatch();
    useTrackBrowserHistory();
    useGetRemoteConfig({ setApiProcessingPool, setApiDonePool });
    useGetCareClientVisitorProfile({ setApiProcessingPool, setApiDonePool });
    useValidateCareClientVisitorProfileAndRedirect({ redirectRoute: null });
    useScrollToTop();
    usePreventInfiniteLoading({ setApiProcessingPool, setApiDonePool });
    const defaultPartTimeExpectedStartTime = useMemo(() => getDefaultPartTimeExpectedStartTime(), [careOrderBabyForm.initialType]);
    const careOrderBabyFormForDateAndTimePage = useMemo(() => {
        const data = CareOrderBabyFormUConverter.getDataForDateAndTimePage({
            careOrderBabyForm,
            maxServiceDays,
        });
        return data;
    }, [careOrderBabyForm, maxServiceDays]);
    const partTimeTimeSlotOptionList = useMemo(() => getPartTimeTimeSlotOptionList({
        ESTBufferHours,
        ESTFurthestDays,
        partTimeExpectedStartTime,
        partTimeExpectedServiceHours: partTimeExpectedServiceHours,
    }), [ESTBufferHours, ESTFurthestDays, partTimeExpectedStartTime, partTimeExpectedServiceHours]);
    useUpdateTimeSelectionDataWhenCarouselActiveItemChange({
        carouselCurrentActiveItemKey,
        carouselPrevActiveItemKey,
        defaultPartTimeExpectedStartTime,
        didSlideCarousel,
        setIsSectionLoading: () => { },
        careOrderPlanOptionList,
        dispatch,
    });
    useResetTimeSelectionDataWhenInitialTypeChange({
        initialType: careOrderBabyForm.initialType,
        timeSlotList: careOrderBabyForm.timeSlotList,
        dispatch,
        defaultPartTimeExpectedStartTime,
        partTimeExpectedStartTime,
    });
    // event handlers
    const handleCarouselActiveItemChange = useCallback(({ current, prev }) => {
        if (!current || !prev)
            return;
        if (!didSlideCarousel) {
            setDidSlideCarousel(true);
        }
        dispatch(trackerCarouselCurrActiveItemKeyUpdate(current));
        dispatch(trackerCarouselPrevActiveItemKeyUpdate(prev));
    }, []);
    const handleIsAsapChoiceChipClick = useCallback(({ isAsap }) => {
        dispatch(careOrderBabyFormUpdate({
            isAsap,
        }));
        if (isAsap) {
            dispatch(careOrderBabyFormUpdateAllDayTimeSlot({
                expectedStartTime: '',
            }));
        }
    }, []);
    const handleAllDayExpectedStartTimeFieldClick = useCallback(() => {
        setPopupType(POPUP_TYPE.ALL_DAY_EXPECTED_START_TIME);
    }, []);
    const handleAllDayExpectedServiceDaysUpdate = useCallback(({ doAdd }) => {
        if (doAdd) {
            const canAdd = getCanAddAllDayExpectedServiceDays({
                allDayExpectedServiceDays,
                maxServiceDays,
            });
            if (!canAdd)
                return;
            dispatch(careOrderBabyFormUpdateAllDayTimeSlot({
                expectedServiceDays: allDayExpectedServiceDays + 1,
            }));
        }
        else {
            const canSubtract = getCanSubtractAllDayExpectedServiceDays({
                allDayExpectedServiceDays,
            });
            if (!canSubtract)
                return;
            dispatch(careOrderBabyFormUpdateAllDayTimeSlot({
                expectedServiceDays: allDayExpectedServiceDays - 1,
            }));
        }
    }, [allDayExpectedServiceDays, maxServiceDays]);
    const handlePartTimeExpectedStartTimeUpdate = useCallback(({ doAdd }) => {
        const canUpdate = doAdd
            ? getCanAddPartTimeExpectedStartTime({
                partTimeExpectedStartTime,
            })
            : getCanSubtractPartTimeExpectedStartTime({
                partTimeExpectedStartTime,
            });
        if (!canUpdate)
            return;
        const minuteUnit = TIME_MANIPULATION.MINUTE.UNIT;
        const timeAfterManipulation = doAdd
            ? dayjs(partTimeExpectedStartTime).add(minuteUnit, 'minute')
            : dayjs(partTimeExpectedStartTime).subtract(minuteUnit, 'minute');
        const payload = timeAfterManipulation.toISOString();
        dispatch(careOrderBabyFormUpdate({
            partTimeExpectedStartTime: payload,
        }));
        dispatch(careOrderBabyFormApplyNewPartTimeExpectedStartTime({
            ESTBufferHours,
            ESTFurthestDays,
            partTimeExpectedStartTime: payload,
            partTimeExpectedServiceHours: partTimeExpectedServiceHours,
        }));
    }, [ESTBufferHours, ESTFurthestDays, partTimeExpectedStartTime, partTimeExpectedServiceHours]);
    const handlePartTimeExpectedStartTimeFieldClick = useCallback(() => {
        setPopupType(POPUP_TYPE.PART_TIME_TIME_SLOT_LIST);
    }, []);
    const handleBackButtonClick = useCallback(() => {
        navigate(APP.ROUTES.NOTES_AND_AGREEMENT);
    }, []);
    const handleForwardButtonClick = () => {
        // #NOTES
        // we do not verify time-related params on this page
        // we handle it on the page where we call the order/init API instead
        navigate(APP.ROUTES.DETAIL_CONFIRMATION);
    };
    const didCompleteRequiredFormFields = getDidCompleteRequiredFormFields({
        isAsap: careOrderBabyForm.isAsap,
        initialType: careOrderBabyForm.initialType,
        timeSlotList: careOrderBabyForm.timeSlotList,
    });
    return (<View 
    // data
    isLoading={isLoading} isAsap={careOrderBabyForm.isAsap} isAllDay={isAllDay} careOrderPlanKey={careOrderBabyForm.careOrderPlanKey} careOrderPlanOptionList={careOrderPlanOptionList} careOrderBabyFormForDateAndTimePage={careOrderBabyFormForDateAndTimePage} isForwardButtonAvailable={didCompleteRequiredFormFields} popupType={popupType} partTimeTimeSlotOptionList={partTimeTimeSlotOptionList} 
    // func
    setPopupType={setPopupType} handleCarouselActiveItemChange={handleCarouselActiveItemChange} handleIsAsapChoiceChipClick={handleIsAsapChoiceChipClick} handleAllDayExpectedStartTimeFieldClick={handleAllDayExpectedStartTimeFieldClick} handleAllDayExpectedServiceDaysUpdate={handleAllDayExpectedServiceDaysUpdate} getCanSubtractAllDayExpectedServiceDays={() => getCanSubtractAllDayExpectedServiceDays({
            allDayExpectedServiceDays,
        })} getCanAddAllDayExpectedServiceDays={() => getCanAddAllDayExpectedServiceDays({
            allDayExpectedServiceDays,
            maxServiceDays,
        })} handlePartTimeExpectedStartTimeUpdate={handlePartTimeExpectedStartTimeUpdate} handlePartTimeExpectedStartTimeFieldClick={handlePartTimeExpectedStartTimeFieldClick} getCanSubtractPartTimeExpectedStartTime={() => getCanSubtractPartTimeExpectedStartTime({ partTimeExpectedStartTime })} getCanAddPartTimeExpectedStartTime={() => getCanAddPartTimeExpectedStartTime({ partTimeExpectedStartTime })} handleBackButtonClick={handleBackButtonClick} handleForwardButtonClick={handleForwardButtonClick}/>);
}
